import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Tree,
    TreeMoveOptions,
    TreeNode,
    TreeNodeData,
    TreeNodeId,
} from 'shared/data_tree/types';
import { insertItems, removeAllItems } from 'utils/array';
import { sentryAddBreadcrumb } from 'utils/sentry';

const RootNodeId = Symbol('root');

type TreeOrRootNodeId = TreeNodeId | typeof RootNodeId;

export type TreeNodeMeta<TData> = Readonly<{
    id: TreeNodeId;
    data: TData;
    parent: TreeNodeId | undefined;
}>;

type TreeMeta<TData> = Readonly<{
    nodes: Map<TreeNodeId, TreeNodeMeta<TData>>;
    children: Map<TreeOrRootNodeId, TreeNodeId[]>;
}>;

function addNodesToMeta<TTreeData>(
    data: TreeNodeData<TTreeData>[],
    parent: TreeNodeId | undefined,
    position: 'before' | 'after',
    insertAt: number,
    meta: TreeMeta<TTreeData>
) {
    data.forEach(({ id, data: itemData, children }) => {
        if (meta.nodes.has(id)) {
            throw new Error(`Duplicate tree item with id "${id}"`);
        }
        meta.nodes.set(id, { id, data: itemData, parent });
        if (children) {
            addNodesToMeta(children, id, 'before', 0, meta);
        }
    });

    const ids = data.map((nodeData) => nodeData.id);
    const parentId = parent ?? RootNodeId;
    const currentChildren = meta.children.get(parentId) ?? [];
    meta.children.set(parentId, insertItems(currentChildren, ids, position, insertAt));
}

function replaceNodesMeta<TTreeData>(
    data: TreeNodeData<TTreeData>[],
    parentId: TreeNodeId,
    meta: TreeMeta<TTreeData>
) {
    for (const { id, data: itemData, children } of data) {
        if (meta.nodes.has(id)) {
            throw new Error(`Duplicate tree item with id "${id}"`);
        }
        meta.nodes.set(id, { id, data: itemData, parent: parentId });
        if (children) {
            replaceNodesMeta(children, id, meta);
        }
    }

    const ids = data.map((nodeData) => nodeData.id);
    meta.children.set(parentId, ids);
}

function replaceNodesMetaNoAlloc<TSourceData, TTreeData>(
    data: TSourceData[],
    parentId: TreeNodeId | undefined,
    meta: TreeMeta<TTreeData>,
    idFn: (o: TSourceData) => string,
    childrenFn: (o: TSourceData) => TSourceData[] | undefined,
    dataFn: (o: TSourceData) => TTreeData
) {
    for (const item of data) {
        const id = idFn(item);
        const itemData = dataFn(item);
        const children = childrenFn(item);
        if (meta.nodes.has(id)) {
            throw new Error(`Duplicate tree item with id "${id}"`);
        }
        meta.nodes.set(id, { id, data: itemData, parent: parentId });
        if (children) {
            replaceNodesMetaNoAlloc(children, id, meta, idFn, childrenFn, dataFn);
        }
    }

    const ids = data.map(idFn);
    meta.children.set(parentId ?? RootNodeId, ids);
}

function createTreeCache<TKey, TItem>() {
    const cache = new Map<TKey, TItem>();

    return {
        get: (key: TKey, valueFn: () => TItem): TItem => {
            const cachedValue = cache.get(key);
            if (cachedValue !== undefined) {
                return cachedValue;
            }
            const value = valueFn();
            cache.set(key, value);
            return value;
        },
        set: (key: TKey, value: TItem) => cache.set(key, value),
    };
}

const cloneMeta = <TData>({ nodes, children }: TreeMeta<TData>): TreeMeta<TData> => ({
    nodes: new Map(nodes.entries()),
    children: new Map(children.entries()),
});

type InitNodeOptions<TData> = Readonly<{
    getTree: () => Tree<TData>;
    nodeMeta: TreeNodeMeta<TData>;
}>;

function initNode<TData>({
    getTree,
    nodeMeta: { id, data },
}: InitNodeOptions<TData>): TreeNode<TData> {
    const node: TreeNode<TData> = {
        id,
        data,
        getTree,
        get level() {
            return getTree().getLevel(id);
        },
        get index() {
            return getTree().getIndex(id);
        },
        get root() {
            return getTree().getRoot(id);
        },
        get isRoot() {
            return getTree().getLevel(id) === 0;
        },
        get isLeaf() {
            return (getTree().getChildren(id)?.length ?? 0) === 0;
        },
        get parent() {
            return getTree().getParent(id);
        },
        get hierarchy() {
            return getTree().getHierarchy(id);
        },
        get children() {
            return getTree().getChildren(id);
        },
        get allChildren() {
            return getTree().getAllChildren(id);
        },
        get selfAndSiblings() {
            return getTree().getSelfAndSiblings(id);
        },
        get siblings() {
            return getTree().getSiblings(id);
        },
        get prevSibling() {
            return getTree().getPrevSibling(id);
        },
        get nextSibling() {
            return getTree().getNextSibling(id);
        },
        remove: () => getTree().remove([id]),
        replace: (newData) => getTree().replace({ id, data: newData }),
        replaceChildren: (childData) => getTree().replaceChildren({ id, childData }),
        clearChildren: () => getTree().clearChildren(id),
        insert: ({ data: insertData, position }) =>
            getTree().insert({
                data: insertData,
                position,
                targetId: id,
            }),
        move: ({ position, targetId }) => getTree().move({ id, position, targetId }),
        toTreeData: () => ({
            id,
            data,
            children: node.children?.map((child) => child.toTreeData()),
        }),
    };

    return node;
}

function initTree<TData>(meta: TreeMeta<TData>): Tree<TData> {
    const caches = {
        nodes: createTreeCache<TreeNodeId, TreeNode<TData>>(),
        roots: createTreeCache<TreeNodeId, TreeNode<TData>>(),
        hierarchy: createTreeCache<TreeNodeId, TreeNode<TData>[]>(),
        children: createTreeCache<TreeOrRootNodeId, TreeNode<TData>[] | undefined>(),
        allChildren: createTreeCache<TreeOrRootNodeId, TreeNode<TData>[]>(),
        selfAndSiblings: createTreeCache<TreeNodeId, TreeNode<TData>[]>(),
        siblings: createTreeCache<TreeNodeId, TreeNode<TData>[]>(),
        index: createTreeCache<TreeNodeId, number>(),
    };

    const getNodeMeta = (id: TreeNodeId) => {
        const nodeMeta = meta.nodes.get(id);
        if (!nodeMeta) {
            throw new Error(`Tree node with id "${id}" not found`);
        }
        return nodeMeta;
    };

    const get = (id: TreeNodeId): TreeNode<TData> =>
        caches.nodes.get(id, () =>
            initNode({ getTree: () => tree, nodeMeta: getNodeMeta(id) })
        );

    const getChildren = (id: TreeNodeId | undefined): TreeNode<TData>[] | undefined => {
        if (id !== undefined && !tree.has(id)) {
            throw new Error(`Tree node with id "${id}" not found`);
        }
        const finalId = id ?? RootNodeId;
        return caches.children.get(finalId, () => {
            const children = meta.children.get(finalId);
            if (children === undefined) {
                return undefined;
            }
            return children.map((childId) => get(childId));
        });
    };

    const getAllChildren = (id: TreeNodeId | undefined): TreeNode<TData>[] =>
        caches.allChildren.get(
            id ?? RootNodeId,
            () =>
                getChildren(id)?.flatMap((child) => [
                    child,
                    ...getAllChildren(child.id),
                ]) ?? []
        );

    const rootNodes = (meta.children.get(RootNodeId) ?? []).map(get);

    const tree: Tree<TData> = {
        rootNodes,
        get allNodes() {
            return getAllChildren(undefined);
        },
        allNodesMeta: () => meta.nodes.values(),
        has: (id) => meta.nodes.has(id),
        hasRoot: (id) => meta.nodes.has(id) && getNodeMeta(id).parent === undefined,
        get,
        getParent: (id) => {
            const nodeMeta = getNodeMeta(id);
            return nodeMeta.parent !== undefined ? get(nodeMeta.parent) : undefined;
        },
        getRoot: (id) =>
            caches.roots.get(id, () => {
                const node = get(id);
                const { parent } = node;
                return parent !== undefined ? tree.getRoot(parent.id) : node;
            }),
        getHierarchy: (id) =>
            caches.hierarchy.get(id, () => {
                const parent = tree.getParent(id);
                return parent ? [parent, ...tree.getHierarchy(parent.id)] : [];
            }),
        getChildren,
        getAllChildren,
        getSelfAndSiblings: (id) =>
            caches.selfAndSiblings.get(
                id,
                () => getChildren(getNodeMeta(id).parent) ?? []
            ),
        getSiblings: (id) =>
            caches.siblings.get(id, () =>
                tree.getSelfAndSiblings(id).filter((node) => node.id !== id)
            ),
        getPrevSibling: (id) => tree.getSelfAndSiblings(id)[tree.getIndex(id) - 1],
        getNextSibling: (id) => tree.getSelfAndSiblings(id)[tree.getIndex(id) + 1],
        getLevel: (id) => tree.getHierarchy(id).length,
        getIndex: (id) =>
            caches.index.get(id, () =>
                tree.getSelfAndSiblings(id).findIndex((node) => node.id === id)
            ),
        remove: (ids) => {
            if (ids.length === 0) {
                return tree;
            }

            const newMeta = cloneMeta(meta);

            const idsToRemove = new Set([
                ...ids,
                ...ids.flatMap((id) => get(id).allChildren.map((child) => child.id)),
            ]);

            for (const id of idsToRemove) {
                newMeta.nodes.delete(id);
                newMeta.children.delete(id);
            }

            for (const [id, children] of newMeta.children) {
                newMeta.children.set(id, removeAllItems(children, ids));
            }
            return initTree(newMeta);
        },
        insertRoot: ({ data, position = 'end' }) => {
            const newMeta = cloneMeta(meta);
            const insertAt = position === 'start' ? 0 : rootNodes.length;

            addNodesToMeta(data, undefined, 'before', insertAt, newMeta);

            return initTree(newMeta);
        },
        insert: ({ data, position, targetId }) => {
            const newMeta = cloneMeta(meta);
            const target = get(targetId);
            const parent = position === 'child' ? targetId : target.parent?.id;
            const insertPosition = position === 'child' ? 'before' : position;
            const insertAt =
                position === 'child' ? target.children?.length ?? 0 : target.index;

            addNodesToMeta(data, parent, insertPosition, insertAt, newMeta);

            return initTree(newMeta);
        },
        clearChildren: (id) => {
            const node = get(id);
            const childIds = node.allChildren.map((child) => child.id);
            const newMeta = cloneMeta(meta);

            childIds.forEach((childId) => {
                newMeta.children.delete(childId);
                newMeta.nodes.delete(childId);
            });
            newMeta.children.delete(id);

            return initTree(newMeta);
        },
        replace: ({ id, data }) => {
            const { prevSibling, nextSibling, parent } = get(id);
            const newTree = tree.remove([id]);

            if (prevSibling) {
                return newTree.insert({
                    data: [data],
                    position: 'after',
                    targetId: prevSibling.id,
                });
            }
            if (nextSibling) {
                return newTree.insert({
                    data: [data],
                    position: 'before',
                    targetId: nextSibling.id,
                });
            }
            if (parent) {
                return newTree.insert({
                    data: [data],
                    position: 'child',
                    targetId: parent.id,
                });
            }
            return newTree.insertRoot({ data: [data] });
        },
        updateAll: ({ ids, updater }) => {
            const newMeta = cloneMeta(meta);

            for (const itemId of ids) {
                // Skip nodes that no longer exist (can happen during rapid tree updates)
                if (!tree.has(itemId)) {
                    sentryAddBreadcrumb({
                        category: 'tree.updateAll',
                        message: 'Skipped non-existent node during updateAll',
                        level: 'info',
                        data: { itemId },
                    });
                    continue;
                }

                const item = get(itemId);

                newMeta.nodes.set(itemId, {
                    id: itemId,
                    data: updater(item.data),
                    parent: item.parent?.id,
                });
            }

            return initTree(newMeta);
        },
        replaceChildren: ({ id: targetId, childData }) => {
            // clear
            const newMeta = cloneMeta(meta);
            const target = get(targetId);

            for (const child of target.allChildren) {
                newMeta.children.delete(child.id);
                newMeta.nodes.delete(child.id);
            }
            newMeta.children.delete(targetId);

            // insert
            replaceNodesMeta(childData, targetId, newMeta);

            return initTree(newMeta);
        },
        replaceChildrenNoAlloc: ({
            id: targetId,
            childData,
            idFn,
            childrenFn,
            dataFn,
        }) => {
            // clear
            const newMeta = cloneMeta(meta);
            const target = get(targetId);

            for (const child of target.allChildren) {
                newMeta.children.delete(child.id);
                newMeta.nodes.delete(child.id);
            }
            newMeta.children.delete(targetId);

            // insert
            replaceNodesMetaNoAlloc(
                childrenFn(childData),
                targetId,
                newMeta,
                idFn,
                childrenFn,
                dataFn
            );

            return initTree(newMeta);
        },
        move: ({ id, position, targetId }: TreeMoveOptions) => {
            const node = get(id);

            const targetIsChild = node.allChildren.some((child) => child.id === targetId);
            if (targetIsChild) {
                throw new Error('Cannot move node to one of its children');
            }

            const data = [node.toTreeData()];
            return tree.remove([id]).insert({ data, position, targetId });
        },
        clear: () => createTree<TData>(),
        toTreeData: () => rootNodes.map((node) => node.toTreeData()),
        mapIf: (condition, transformer) => (condition ? transformer(tree) : tree),
    };

    // Used to view the internal metadata of the tree.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tree as any).debug = () => meta;

    return tree;
}

export function createTree<TData>(rootData?: TreeNodeData<TData>[]): Tree<TData> {
    const meta: TreeMeta<TData> = {
        nodes: new Map(),
        children: new Map(),
    };

    if (rootData) {
        addNodesToMeta(rootData, undefined, 'before', 0, meta);
    }

    return initTree(meta);
}

export function createFullTree<TSourceData, TTreeData>(
    rootData: TSourceData[],
    idFn: (o: TSourceData) => string,
    childrenFn: (o: TSourceData) => TSourceData[] | undefined,
    dataFn: (o: TSourceData) => TTreeData
): Tree<TTreeData> {
    const meta: TreeMeta<TTreeData> = {
        nodes: new Map(),
        children: new Map(),
    };

    replaceNodesMetaNoAlloc(rootData, undefined, meta, idFn, childrenFn, dataFn);

    return initTree(meta);
}

type UseTreeOptions<TSourceData, TTreeData> = {
    rootTreeItem: TSourceData | undefined;
    idFn: (o: TSourceData) => string;
    childrenFn: (o: TSourceData) => TSourceData[] | undefined;
    dataFn: (o: TSourceData) => TTreeData;
    resetOnNewRoot?: boolean;
};

type SetTree<TData> = (current: (setStateAction: Tree<TData>) => Tree<TData>) => void;
export type UseHierarchyTreeReturn<TData> = {
    tree: Tree<TData>;
    setTree: (current: (setStateAction: Tree<TData>) => Tree<TData>) => void;
};

/**
 * Handles initializing a tree and exposes mechanisms for retrieving and setting
 * its state. Any time `rootTreeItem` updates, the tree will be rebuilt using
 * the new data.
 */
export function useFullTree<TSourceData, TTreeData>({
    rootTreeItem,
    idFn,
    childrenFn,
    dataFn,
    resetOnNewRoot = false,
}: UseTreeOptions<TSourceData, TTreeData>): UseHierarchyTreeReturn<TTreeData> {
    const fnsRef = useRef({
        idFn,
        childrenFn,
        dataFn,
    });
    const [tree, _setTree] = useState(() => createTree<TTreeData>());
    const treeRef = useRef(tree);
    const setTree = useMemo<SetTree<TTreeData>>(
        () => (setStateAction) => {
            _setTree((cur) => {
                const val = setStateAction(cur);
                treeRef.current = val;
                return val;
            });
        },
        []
    );

    useEffect(() => {
        if (rootTreeItem === undefined) {
            return;
        }
        const id = fnsRef.current.idFn(rootTreeItem);
        if (rootTreeItem && (resetOnNewRoot || !treeRef.current.hasRoot(id))) {
            setTree(() =>
                createFullTree<TSourceData, TTreeData>(
                    [rootTreeItem],
                    fnsRef.current.idFn,
                    fnsRef.current.childrenFn,
                    fnsRef.current.dataFn
                )
            );
        }
    }, [resetOnNewRoot, rootTreeItem, setTree]);

    return useMemo(
        () => ({
            tree,
            setTree,
        }),
        [setTree, tree]
    );
}

export type BuildTreeDataOptions<TData> = Readonly<{
    /**
     * The data for the level of the tree to build.
     */
    data: TData[];
    /**
     * A function for getting the key for the tree node.
     */
    dataKey: (item: TData) => string;
    /**
     * A function for getting the children of the tree node.
     */
    children?: (parent: TData) => TData[] | undefined;
}>;

/**
 * Utility for recursively building a tree from a flat list of data.
 *
 * @returns A tree structure that can be passed to the `createTree` function.
 */
export function buildTreeData<TData>({
    data,
    dataKey,
    children,
}: BuildTreeDataOptions<TData>): TreeNodeData<TData>[] {
    return data.map<TreeNodeData<TData>>((node) => {
        const childData = children?.(node);

        const treeData: TreeNodeData<TData> = {
            id: dataKey(node),
            data: node,
            children: childData
                ? buildTreeData({ data: childData, dataKey, children })
                : undefined,
        };
        return treeData;
    });
}

export type FilterTreeDataOptions<TData> = Readonly<{
    tree: Tree<TData>;
    where: (node: TreeNode<TData>) => boolean;
}>;

/**
 * Utility for filtering tree data based on a given predicate (ex. a search function)
 * @returns A new tree representing the filtered tree items and a list of the hierarchy items
 * that should be expanded.
 */
export function filterTreeData<TData>({ tree, where }: FilterTreeDataOptions<TData>) {
    const nodeIdsToRemove = tree.allNodes
        .filter(
            (node) =>
                !where(node) &&
                !node.allChildren.some(where) &&
                !node.hierarchy.some(where)
        )
        .map((node) => node.id);

    const filteredTree = tree.remove(nodeIdsToRemove);

    const expandedIds = filteredTree.allNodes
        .filter((node) => node.allChildren.some(where))
        .map((node) => node.id);

    return {
        filteredTree,
        expandedIds,
    };
}
